import {
    VOTED_FIELD,
    REALIZED_FIELD,
    VOTED_TOTAL_FIELD,
    SPLIT_BY,
    REPORTED_FIELD,
    PRIMITIVE_BUDGET_AMOUNT,
} from 'routes/BudgetModule/config/constants';
import { currencyFormatterRound } from 'ui/AgGridReact/formatters';
import { translateBlockValue } from 'i18n';
import {
    amountMetricToPhase,
    getFieldForPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { ORDERED_LIST_METRIC } from 'routes/BudgetModule/components/BudgetOverview/AllOperations/helpers/dataHelpers';
import {
    CellClassParams,
    CellStyle,
    ColDef,
    ColGroupDef,
} from 'ag-grid-community';
import { EXPENSE_INCOME_AND_SECTION } from './dataHelpers';
import { BORDER_LEFT } from '../helpers/constants';

function isFirstColumn(cell: CellClassParams<unknown, unknown>) {
    return (
        cell.column.getUniqueId() ===
        cell.column.getParent()?.getDisplayedChildren()?.[0].getUniqueId()
    );
}

const buildChild = (
    headerName: string,
    field: string,
    defaultStyle: CellStyle,
): ColDef => ({
    field,
    headerName,
    sortable: true,
    valueFormatter: (params) => currencyFormatterRound(params) ?? '',
    cellStyle: (cell) =>
        isFirstColumn(cell)
            ? {
                  ...defaultStyle,
                  ...BORDER_LEFT,
              }
            : defaultStyle,
    flex: 2,
    resizable: true,
});

/** This Function is used to retrieve the Columns inside the operation view */
export const getColumns = (
    exercice: number,
    exerciceRange: number,
    primaryColor: string,
    t: TranslationT,
): (ColDef | ColGroupDef)[] => {
    const cellStylePrimary = {
        color: primaryColor,
        fontWeight: 400,
        justifyContent: 'flex-end',
    };
    const cellStyleNormal = { fontWeight: 400, justifyContent: 'flex-end' };
    const cellStyleSemiBold = { fontWeight: 510 };

    const columns: (ColDef | ColGroupDef)[] = [
        {
            field: 'name',
            headerName: '',
            sortable: true,
            enableRowGroup: true,
            cellStyle: cellStyleSemiBold,
            flex: 1,
            resizable: true,
            valueFormatter: (value) =>
                EXPENSE_INCOME_AND_SECTION.includes(value.value)
                    ? value.value
                          .split(' ')
                          .map(translateBlockValue)
                          .map((s: string) => s.slice(0, 3))
                          .join(' - ')
                    : value.value,
        },
    ];

    // -------- Fields depending on exercice amount -------- //
    const fields = [VOTED_FIELD, REALIZED_FIELD, VOTED_TOTAL_FIELD];

    for (let i = exerciceRange; i >= 1; i -= 1) {
        const headerName = (exercice - i).toString();
        const children: ColDef[] = [];
        fields.forEach((field) => {
            children.push(
                buildChild(
                    translateBlockValue(field, true),
                    `${field}${SPLIT_BY}${exercice - i}`,
                    cellStyleNormal,
                ),
            );
        });
        columns.push({ headerName, children });
    }

    // -------- Fields depending on the operation budget's phase -------- //
    const headerName = exercice.toString();
    const children: ColDef[] = [];
    ORDERED_LIST_METRIC.forEach((metricPhase) => {
        const phase = amountMetricToPhase[metricPhase];
        const header = getFieldForPhase(phase, 'getPhaseHeaderName')(
            exercice,
            t,
        );
        children.push(buildChild(header, metricPhase, cellStylePrimary));
        if (metricPhase === PRIMITIVE_BUDGET_AMOUNT) {
            children.push(
                buildChild(
                    translateBlockValue(REPORTED_FIELD, true),
                    `${REPORTED_FIELD}${SPLIT_BY}${exercice}`,
                    cellStyleNormal,
                ),
            );
        }
    });

    columns.push({ headerName, children });
    return columns;
};
